import { Booking } from '../types/qraphql/booking.type';
import { isFuture, isWithinInterval, parseISO, parseJSON } from 'date-fns';
import _ from 'lodash';
import { BookingKindEnum } from '../types/booking-kind.enum';
import { toDate } from 'date-fns-tz';
import { getDefaultLocation, getLocation } from './booking-location';
import { BookingTypeEnum } from '../types/booking-type.enum';
import { adjustForTimezone } from './date';
import { logger } from '../services/logger.service';

/**
 * Check if the particular booking starts at a future date.
 * @param {Booking} booking
 * @return {boolean}
 */
export const isFutureBooking = (booking: Booking): boolean => {
    const location = getLocation(booking) || getDefaultLocation();
    return isFuture(toDate(booking.from, { timeZone: location.timezone }));
};

/**
 * Get the booking kind
 * @param {boolean} isFutureBooking
 * @return {BookingKindEnum}
 */
export const getBookingKind = (isFutureBooking: boolean): BookingKindEnum => {
    if (isFutureBooking) {
        return BookingKindEnum.PRE_ARRIVAL;
    } else {
        return BookingKindEnum.IN_STAY;
    }
};

/**
 * Checks whether the reservation is currently active or not.
 * @param {Booking} booking
 * @return {boolean}
 */
export const isActiveBooking = (booking: Booking): boolean => {
    if (booking.property.ianaCode === undefined) {
        logger.error(new Error("Booking's property timezone is missing"));
        return false;
    }

    return isWithinInterval(new Date(), {
        start: adjustForTimezone(
            parseISO(booking.from),
            booking.property.ianaCode,
        ),
        end: adjustForTimezone(parseISO(booking.to), booking.property.ianaCode),
    });
};

/**
 * Sort future bookings by "from" field in ascending order
 * @param {Booking[]} bookings
 * @return {Booking[]}
 */
export const getOrderedFutureBookings = (
    bookings: Booking[] = [],
): Booking[] => {
    return _.orderBy(
        bookings.filter((booking) => isFutureBooking(booking)),
        ['from'],
        ['asc'],
    );
};

/**
 * Get active booking
 * @param {Booking[]} bookings
 * @return {Booking | undefined}
 */
export const getActiveBooking = (
    bookings: Booking[] = [],
): Booking | undefined => {
    return bookings.find((booking) => isActiveBooking(booking));
};

/**
 * Get bookings by location
 * @param {Booking[]} bookings
 * @param {string} propertyId
 * @return {Booking[]}
 */
export const getBookingsByLocation = (
    bookings: Booking[] = [],
    propertyId: string,
): Booking[] => {
    return bookings.filter((booking) => booking.property.id === propertyId);
};

/**
 * Get future bookings
 * @param {Booking[]} bookings
 * @return {Booking[]}
 */
export const getFutureBookings = (bookings: Booking[] = []): Booking[] => {
    return bookings.filter((booking) => isFuture(parseJSON(booking.to)));
};

/**
 * Get future bookings by location
 * @param {Booking[]} bookings
 * @param {string} propertyId
 * @return {Booking[]}
 */
export const getFutureBookingsByLocation = (
    bookings: Booking[] = [],
    propertyId: string,
): Booking[] => {
    return getBookingsByLocation(getFutureBookings(bookings), propertyId);
};

/**
 * Returns the default selected Booking.
 * @param {Booking[]} bookings
 * @return {Booking}
 */
export const getDefaultBooking = (bookings: Booking[] = []): Booking => {
    const futureBookings = getOrderedFutureBookings(bookings);
    const activeBooking = getActiveBooking(bookings);
    if (activeBooking) {
        return activeBooking;
    } else if (futureBookings.length > 0) {
        return futureBookings[0];
    } else {
        return _.orderBy(bookings, ['from'], ['desc'])[0];
    }
};

/**
 * Returns whether any of the bookings are OWP (Optimal Wellbeing Program).
 * TODO Blocked by SD-1414
 *  - Update the GraphQL schema.
 *  - Update the "Booking.bookingType" type to ENUM.
 *  - Move PERFORMANCE_BOOKING_TYPE to Enum and add other booking types too.
 *  - Refactor function to be able to check any booking type not just OWP.
 * @param {Booking[]} bookings
 * @return {boolean}
 */
export const isAnyOfTheBookingsOwp = (bookings: Booking[] = []): boolean => {
    return bookings.some((booking) => isBookingOwp(booking));
};

/**
 * Returns whether the booking is OWP (Optimal Wellbeing Program).
 * TODO Blocked by SD-1414
 *  - Update the GraphQL schema.
 *  - Update the "Booking.bookingType" type to ENUM.
 *  - Move PERFORMANCE_BOOKING_TYPE to Enum.
 *  - Refactor function to be able to check any booking type not just OWP.
 * @param {Booking} booking
 * @return {boolean}
 */
const owpBookingTypes = [
    BookingTypeEnum.PERFORMANCE,
    BookingTypeEnum.OWP_GOLF,
    BookingTypeEnum.OWP_TENNIS,
];
export const isBookingOwp = (booking: Booking): boolean => {
    return owpBookingTypes.indexOf(booking.bookingType) !== -1;
};

/**
 * Returns whether the Active OR Next booking is OWP (Optimal Wellbeing Program).
 * The Next booking will only be checked if there is no active booking.
 * TODO Blocked by SD-1414
 *  - Update the GraphQL schema.
 *  - Update the "Booking.bookingType" type to ENUM.
 *  - Move PERFORMANCE_BOOKING_TYPE to Enum.
 *  - Refactor function to be able to check any booking type not just OWP.
 * @param {Booking} bookings
 * @return {boolean}
 */
export const isActiveOrNextBookingIsOwp = (
    bookings: Booking[] = [],
): boolean => {
    const activeBooking = getActiveBooking(bookings);
    const futureBookings = getOrderedFutureBookings(bookings);
    if (activeBooking) {
        return activeBooking && isBookingOwp(activeBooking);
    } else if (futureBookings.length > 0) {
        return isBookingOwp(futureBookings[0]);
    } else {
        return false;
    }
};
